import { useRef, useState } from "react"
import {
  ActivityIndicator,
  Keyboard,
  Pressable,
  TextInput,
  TextStyle,
  View,
  ViewStyle,
} from "react-native"

import { Ionicons } from "@expo/vector-icons"
import { router, useLocalSearchParams } from "expo-router"
import { KeyboardAwareScrollView } from "react-native-keyboard-controller"
import { SafeAreaView } from "react-native-safe-area-context"

import { Alert } from "src/components/Alert"
import {
  Button,
  ButtonAccessoryProps,
  PasswordTextField,
  Text,
  TextField,
} from "src/components/Ignite"
import { useAuth } from "src/context/AuthContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"
import { logSentryBreadcrumb } from "src/utils/crashReporting"
import { getPasswordValidationError } from "src/utils/validatePassword"

const PASSWORD_RULES =
  "required: upper; required: digit; required: special; required: length: min=8; required: lower;"

type TouchedFields = {
  email: boolean
  oldPassword: boolean
  authPassword: boolean
  authPasswordVerify: boolean
}

export default function UpdatePassword() {
  const { themed, theme, themeContext } = useAppTheme()
  const { isAuthenticated } = useAuth()
  const [showUpdateMessage] = useState(!isAuthenticated)

  const { email: initialEmail, password: initialPassword } = useLocalSearchParams<{
    email: string
    password: string
  }>()

  const oldPasswordInput = useRef<TextInput>(null)
  const authPasswordInput = useRef<TextInput>(null)
  const authPasswordVerifyInput = useRef<TextInput>(null)
  const prevOldPasswordLengthRef = useRef(0)

  const [email] = useState(initialEmail)
  const [oldPassword, setOldPassword] = useState(initialPassword || "")
  const [authPassword, setAuthPassword] = useState("")
  const [authPasswordVerify, setAuthPasswordVerify] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  // Two-step form: step 1 = current password, step 2 = new password.
  // Only used in the authenticated (menu) flow; forced-update flow stays single-step.
  const [step, setStep] = useState<1 | 2>(1)

  const [touchedFields, setTouchedFields] = useState<TouchedFields>({
    email: false,
    oldPassword: false,
    authPassword: false,
    authPasswordVerify: false,
  })

  const authPasswordError = getPasswordValidationError(authPassword)
  const authPasswordVerifyError = getPasswordValidationError(authPasswordVerify, authPassword)

  const showAuthPasswordError = touchedFields.authPassword || isLoading
  const showAuthPasswordVerifyError = touchedFields.authPasswordVerify || isLoading

  const { signIn, changePassword } = useAuth()

  const onLogin = async () => {
    try {
      setIsLoading(true)
      logSentryBreadcrumb("login_attempt")
      const response = await signIn({ email, password: authPassword })

      if (response.kind !== "ok") {
        const errorMessage = response.message || "An unknown error occurred. Please try again."
        Alert.alert("Error", errorMessage)
      } else {
        logSentryBreadcrumb("login_success")
        router.replace("/")
      }
    } catch (error: any) {
      console.log("Login Error", error)
      logSentryBreadcrumb("login_error")
      Alert.alert("Error", "An unknown error occurred. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const onOldPasswordChange = (text: string) => {
    const prevLength = prevOldPasswordLengthRef.current
    prevOldPasswordLengthRef.current = text.length
    setOldPassword(text)

    // Autofill and paste both deliver the full value in a single onChangeText call,
    // producing a jump of more than 1 character. Regular typing increments by 1.
    // When we detect a fill event, dismiss the keyboard so the Continue button is
    // immediately visible and tappable.
    if (isAuthenticated && text.length > 0 && text.length - prevLength > 1) {
      Keyboard.dismiss()
    }
  }

  // Dismiss the keyboard before transitioning so step 2 renders cleanly.
  // We intentionally do NOT auto-focus the new password field — doing so
  // races against iOS's keyboard animation and causes "Use Strong Password"
  // to flash. The user tapping the field naturally triggers the suggestion
  // without any visual interruption.
  const onContinueToStep2 = () => {
    if (!oldPassword) {
      Alert.alert("Error", "Please enter your current password")
      return
    }
    Keyboard.dismiss()
    setStep(2)
  }

  const onChangePassword = async () => {
    setTouchedFields({
      email: true,
      oldPassword: true,
      authPassword: true,
      authPasswordVerify: true,
    })

    const hasEmail = !!email
    const hasOldPassword = !!oldPassword
    const newPasswordError = getPasswordValidationError(authPassword)
    const verifyPasswordError = getPasswordValidationError(authPasswordVerify, authPassword)

    if (!hasEmail || !hasOldPassword || newPasswordError || verifyPasswordError) {
      let alertText = "Error changing your password"
      if (!hasEmail) {
        alertText = "Please enter your email"
      } else if (!hasOldPassword) {
        alertText = "Please enter your old password"
      } else if (newPasswordError) {
        alertText = "Please enter a valid new password"
      } else if (verifyPasswordError) {
        alertText = "Please verify your new password fields match."
      }
      Alert.alert("Error", alertText)
      return
    }

    try {
      setIsLoading(true)
      logSentryBreadcrumb("login_attempt")
      const response = await changePassword({
        email,
        oldPassword,
        newPassword: authPassword,
      })
      if (response.kind !== "ok") {
        throw new Error(response.message || "Password change failed")
      }
      logSentryBreadcrumb("change_password_success")

      if (!isAuthenticated) {
        await onLogin()
      } else {
        router.replace("/")
      }
    } catch (error: any) {
      console.log("Change Password Error", error)
      logSentryBreadcrumb("change_password_error")
      const errorMessage = error?.message || "Error changing your password"
      Alert.alert("Error", errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    Keyboard.dismiss()
    if (isAuthenticated && step === 2) {
      setStep(1)
    } else {
      router.dismissTo("/")
    }
  }

  // Authenticated (menu) flow: step 1 = current password, step 2 = new password.
  // Forced-update flow (!isAuthenticated): email + old password arrive via params,
  // so go straight to step 2 (new password only).
  const showOldPasswordForm = isAuthenticated && step === 1
  const showNewPasswordForm = !isAuthenticated || step === 2

  const headingText = isAuthenticated && step === 2 ? "Set New Password" : "Update Your Password"

  return (
    <View style={$flex}>
      {/* Back button: always shown in authenticated flow; never in forced-update flow */}
      {isAuthenticated && (
        <SafeAreaView edges={["top"]} style={themed($header)}>
          <Pressable
            onPress={handleBack}
            style={({ pressed }) => [themed($backButton), pressed && themed($pressed)]}
          >
            <Ionicons name="arrow-back" size={24} style={themed($iconColor)} />
            <Text style={themed($backButtonText)}>Back</Text>
          </Pressable>
        </SafeAreaView>
      )}

      <KeyboardAwareScrollView style={themed($screenContentContainer)}>
        <View style={themed($titleContainer)}>
          <Text
            testID="update-password-heading"
            text={headingText}
            preset="heading"
            style={themed($heading)}
          />
          {showUpdateMessage && (
            <Text style={themed($subHeading)}>
              Password Update Required: For your security, you need to change your password before
              proceeding. Please follow the instructions to set a new password.
            </Text>
          )}
        </View>

        <View style={themed($formContainer)}>
          {/* ── Step 1: current password ──────────────────────────── */}
          {showOldPasswordForm && (
            <>
              <View pointerEvents="none">
                <TextField
                  label="Email:"
                  style={themed($input)}
                  inputWrapperStyle={themed($inputWrapper)}
                  value={email}
                  editable={false}
                  onChangeText={() => {}}
                  autoComplete="username"
                  textContentType="username"
                  importantForAutofill="auto"
                />
              </View>
              <PasswordTextField
                style={[themed($input), themeContext === "dark" && $passwordInputDarkShadow]}
                inputWrapperStyle={themed($inputWrapper)}
                ref={oldPasswordInput}
                value={oldPassword}
                onChangeText={onOldPasswordChange}
                onBlur={() => setTouchedFields((prev) => ({ ...prev, oldPassword: true }))}
                placeholderTx="loginScreen.passwordInputPlaceholder"
                label="Current Password:"
                autoFocus
                onSubmitEditing={onContinueToStep2}
                returnKeyType="go"
                autoComplete="current-password"
                textContentType="password"
                importantForAutofill="auto"
              />
              {isAuthenticated && (
                <Button
                  text="Continue"
                  preset="sidekick"
                  onPress={onContinueToStep2}
                  disabled={isLoading}
                />
              )}
            </>
          )}

          {/* ── Step 2: new password ───────────────────────────────── */}
          {showNewPasswordForm && (
            <>
              {/* iOS requires an editable username field alongside newPassword to offer
                  "Use Strong Password". editable={true} keeps it visible to the autofill
                  engine; pointerEvents="none" prevents the user from tapping or editing it.
                  "Use Strong Password" does not overwrite the username field, so this is safe. */}
              <View pointerEvents="none">
                <TextField
                  label="Email:"
                  style={themed($input)}
                  inputWrapperStyle={themed($inputWrapper)}
                  value={email}
                  onChangeText={() => {}}
                  autoComplete="username"
                  textContentType="username"
                  importantForAutofill="yes"
                />
              </View>
              <PasswordTextField
                style={[themed($input), themeContext === "dark" && $passwordInputDarkShadow]}
                inputWrapperStyle={themed($inputWrapper)}
                ref={authPasswordInput}
                value={authPassword}
                onChangeText={setAuthPassword}
                onBlur={() => setTouchedFields((prev) => ({ ...prev, authPassword: true }))}
                placeholderTx="loginScreen.passwordInputPlaceholder"
                label="New Password:"
                helper={showAuthPasswordError ? authPasswordError : ""}
                status={showAuthPasswordError && authPasswordError ? "error" : undefined}
                autoFocus={!isAuthenticated}
                onSubmitEditing={() => authPasswordVerifyInput.current?.focus()}
                returnKeyType="next"
                autoComplete="new-password"
                textContentType="newPassword"
                passwordRules={PASSWORD_RULES}
                importantForAutofill="yes"
              />
              <PasswordTextField
                style={[themed($input), themeContext === "dark" && $passwordInputDarkShadow]}
                inputWrapperStyle={themed($inputWrapper)}
                ref={authPasswordVerifyInput}
                value={authPasswordVerify}
                onChangeText={setAuthPasswordVerify}
                onBlur={() => setTouchedFields((prev) => ({ ...prev, authPasswordVerify: true }))}
                placeholderTx="loginScreen.passwordVerifyInputPlaceholder"
                label="Verify New Password:"
                helper={
                  showAuthPasswordVerifyError
                    ? authPasswordError
                      ? ""
                      : authPasswordVerifyError
                    : ""
                }
                status={
                  showAuthPasswordVerifyError && authPasswordVerifyError ? "error" : undefined
                }
                onSubmitEditing={onChangePassword}
                returnKeyType="go"
                autoComplete="new-password"
                textContentType="newPassword"
                passwordRules={PASSWORD_RULES}
                importantForAutofill="yes"
              />
              <Button
                testID="update-password-button"
                text={isLoading ? "" : "Set New Password"}
                preset="sidekick"
                onPress={onChangePassword}
                disabled={isLoading}
                RightAccessory={
                  isLoading
                    ? (_: ButtonAccessoryProps) => (
                        <ActivityIndicator color={theme.colors.menuButtonText} />
                      )
                    : undefined
                }
              />
            </>
          )}
        </View>
      </KeyboardAwareScrollView>
    </View>
  )
}

const $flex: ViewStyle = {
  flex: 1,
}

const $screenContentContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flex: 1,
  paddingVertical: spacing.xxl,
  paddingHorizontal: spacing.lg,
})

const $titleContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  justifyContent: "center",
  alignItems: "center",
  marginTop: spacing.xxxs,
})

const $formContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  gap: spacing.md,
  marginTop: spacing.xxl,
  marginBottom: spacing.xl,
})

const $heading: ThemedStyle<TextStyle> = ({ spacing }) => ({
  marginBottom: spacing.xxxs,
  textAlign: "center",
})

const $subHeading: ThemedStyle<TextStyle> = () => ({
  fontSize: 16,
})

const $input: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.inputText,
})

// iOS autofill overlays a yellow highlight regardless of the field's background
// color. In dark mode, white text on yellow is near-invisible. A dark text shadow
// acts as an outline, staying invisible on the dark input background but making
// white characters legible against the yellow autofill highlight.
const $passwordInputDarkShadow: TextStyle = {
  textShadowColor: "rgba(0,0,0,1)",
  textShadowOffset: { width: 0, height: 0 },
  textShadowRadius: 8,
}

const $inputWrapper: ThemedStyle<ViewStyle> = ({ colors }) => ({
  borderColor: colors.inputBorder,
  borderWidth: 2,
})

const $header: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  paddingHorizontal: spacing.lg,
  paddingBottom: spacing.sm,
})

const $backButton: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xs,
})

const $pressed: ThemedStyle<ViewStyle> = () => ({
  opacity: 0.7,
})

const $iconColor: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.text,
})

const $backButtonText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.text,
  fontSize: 16,
})
